/**
 * Translations registered per package, not from one central bundle.
 *
 * Each `feature-*` package ships its own namespace and its own locale files,
 * exactly as the Flutter base template gives each Dart package its own ARB
 * files. Two things follow, and both matter more than they sound:
 *
 * - **Deleting a feature deletes its translations.** A central bundle
 *   accumulates keys for features that no longer exist, and nobody ever dares
 *   remove them because nobody can prove they are unused.
 * - **A fork adding a feature adds a namespace**, and never edits a file the
 *   upstream template also edits — which is what makes pulling upstream
 *   changes a clean cherry-pick rather than a merge conflict in one enormous
 *   JSON file.
 */

import type { Locale } from "./config";

/** One package's translations for every locale it ships. */
export type NamespaceBundle = Partial<Record<Locale, Record<string, unknown>>>;

const registry = new Map<string, NamespaceBundle>();

/**
 * Register a package's translations.
 *
 * Called at module load by each `feature-*` package. Registering the same
 * namespace twice is a mistake worth failing on: it means two packages claim
 * one name, and which one wins would depend on import order.
 */
export function registerNamespace(namespace: string, bundle: NamespaceBundle): void {
  if (registry.has(namespace)) {
    throw new Error(`The i18n namespace "${namespace}" is already registered.`);
  }
  registry.set(namespace, bundle);
}

export function registeredNamespaces(): string[] {
  return [...registry.keys()];
}

/**
 * One locale's namespaces, in the shape i18next calls a `ResourceLanguage`.
 *
 * Typed here rather than imported from i18next so the registry stays usable
 * without it — the registry is a plain map, and a test that only checks key
 * parity should not have to boot a translation engine.
 */
export type LocaleResources = Record<string, Record<string, unknown>>;

/** Flatten the registry into the shape i18next expects. */
export function resourcesForI18next(): Record<string, LocaleResources> {
  const resources: Record<string, LocaleResources> = {};
  for (const [namespace, bundle] of registry) {
    for (const [locale, translations] of Object.entries(bundle)) {
      resources[locale] ??= {};
      resources[locale][namespace] = translations;
    }
  }
  return resources;
}

/** Reset the registry. Tests only — module state otherwise leaks between them. */
export function clearRegistry(): void {
  registry.clear();
}
